import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { 
  Users, TrendingUp, Mail, Phone, Building2, Target, 
  Clock, CheckCircle2, XCircle, MessageSquare, Loader2,
  ChevronDown, ChevronUp, ExternalLink, FileDown, Send, RefreshCw, Trash2,
  UserPlus, FileText, Upload
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { API, authFetch } from "@/App";
import { toast } from "sonner";

export const LeadsPanel = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedLead, setExpandedLead] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [deletingLead, setDeletingLead] = useState(null);
  const [leadSource, setLeadSource] = useState("website");
  const [sendingBlast, setSendingBlast] = useState(null);
  
  // Convert to Client state
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [convertingLead, setConvertingLead] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [selectedConsultant, setSelectedConsultant] = useState("");
  const [converting, setConverting] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    loadLeads();
    loadConsultants();
  }, [leadSource]);

  const loadConsultants = async () => {
    try {
      const res = await authFetch(`${API}/assessment/consultants`);
      if (res.ok) {
        const data = await res.json();
        setConsultants(data.consultants || []);
      }
    } catch (error) {
      console.error("Error loading consultants:", error);
    }
  };

  const loadLeads = async () => {
    try {
      setLoading(true);
      const sourceParam = leadSource !== "all" ? `?source=${leadSource}` : "";
      const [leadsRes, statsRes] = await Promise.all([
        authFetch(`${API}/assessment/leads${sourceParam}`),
        authFetch(`${API}/assessment/leads/stats`)
      ]);
      
      if (leadsRes.ok) {
        const data = await leadsRes.json();
        setLeads(data.leads || []);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Error loading leads:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (assessmentId, newStatus) => {
    try {
      const res = await authFetch(`${API}/assessment/leads/${assessmentId}/status?status=${newStatus}`, {
        method: 'PATCH'
      });
      
      if (res.ok) {
        toast.success(`Lead status updated to ${newStatus}`);
        loadLeads();
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      toast.error("Error updating lead status");
    }
  };

  const resendResultsEmail = async (assessmentId, email) => {
    setSendingEmail(assessmentId);
    try {
      const res = await authFetch(`${API}/assessment/leads/${assessmentId}/resend-results`, {
        method: 'POST'
      });
      
      if (res.ok) {
        toast.success(`Results sent to ${email}`);
        loadLeads(); // Refresh to show resent timestamp
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to send email");
      }
    } catch (error) {
      toast.error("Error sending email");
    } finally {
      setSendingEmail(null);
    }
  };

  const downloadPdf = async (assessmentId, name) => {
    setDownloadingPdf(assessmentId);
    try {
      const res = await authFetch(`${API}/assessment/leads/${assessmentId}/pdf`);
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `assessment_${name.replace(/\s+/g, '_')}_${assessmentId.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("PDF downloaded");
      } else {
        toast.error("Failed to download PDF");
      }
    } catch (error) {
      toast.error("Error downloading PDF");
    } finally {
      setDownloadingPdf(null);
    }
  };

  const deleteLead = async (assessmentId, name) => {
    setDeletingLead(assessmentId);
    try {
      const res = await authFetch(`${API}/assessment/leads/${assessmentId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success(`Lead "${name}" deleted successfully`);
        loadLeads(); // Refresh the list
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to delete lead");
      }
    } catch (error) {
      toast.error("Error deleting lead");
    } finally {
      setDeletingLead(null);
    }
  };

  const openConvertDialog = (lead) => {
    setConvertingLead(lead);
    setSelectedConsultant("");
    setShowConvertDialog(true);
  };

  const handleConvertToClient = async () => {
    if (!selectedConsultant) {
      toast.error("Please select a consultant to assign");
      return;
    }

    setConverting(true);
    try {
      const res = await authFetch(`${API}/assessment/leads/${convertingLead.assessment_id}/convert-to-client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          consultant_id: selectedConsultant,
          send_nda_proposal: true
        })
      });

      if (res.ok) {
        toast.success(`NDA proposal sent to ${convertingLead.name}! They will receive an email to accept.`);
        setShowConvertDialog(false);
        loadLeads();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to convert lead");
      }
    } catch (error) {
      toast.error("Error converting lead");
    } finally {
      setConverting(false);
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const res = await authFetch(`${API}/assessment/leads/import-csv`, {
        method: "POST",
        body: JSON.stringify({ csv_content: text }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        loadLeads();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Import failed");
      }
    } catch (err) {
      toast.error("Failed to import CSV");
    } finally {
      setImporting(false);
      e.target.value = "";
    }
  };


  const sendLaunchEmail = async () => {
    if (!confirm("Send launch invitation email to all imported leads? This will email every imported contact.")) return;
    setSendingBlast("launch");
    try {
      const res = await authFetch(`${API}/assessment/leads/send-launch-email`, {
        method: "POST", body: JSON.stringify({ target: "imported" })
      });
      if (res.ok) { const d = await res.json(); toast.success(d.message); loadLeads(); }
      else toast.error("Failed to send");
    } catch { toast.error("Failed"); }
    setSendingBlast(null);
  };

  const sendDomainUpdate = async () => {
    if (!confirm("Send domain update email to all existing members? This will notify all clients about www.mybizconsulting.net.")) return;
    setSendingBlast("domain");
    try {
      const res = await authFetch(`${API}/assessment/leads/send-domain-update`, {
        method: "POST", body: JSON.stringify({})
      });
      if (res.ok) { const d = await res.json(); toast.success(d.message); }
      else toast.error("Failed to send");
    } catch { toast.error("Failed"); }
    setSendingBlast(null);
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-700';
      case 'contacted': return 'bg-amber-100 text-amber-700';
      case 'converted': return 'bg-green-100 text-green-700';
      case 'proposal_sent': return 'bg-purple-100 text-purple-700';
      case 'lost': return 'bg-red-100 text-red-700';
      default: return 'bg-neutral-100 text-neutral-700';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 40) return 'text-amber-600';
    return 'text-red-600';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-neutral-500">Total Leads</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.website_leads || 0}</p>
                  <p className="text-sm text-neutral-500">Website</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Upload className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.imported_leads || 0}</p>
                  <p className="text-sm text-neutral-500">Imported</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.by_status?.new || 0}</p>
                  <p className="text-sm text-neutral-500">New</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.by_status?.converted || 0}</p>
                  <p className="text-sm text-neutral-500">Converted</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Email Blast Actions */}
      <Card className="border-neutral-200 bg-neutral-50">
        <CardContent className="p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Send className="w-5 h-5 text-[#2BC40E]" />
            <span className="font-medium text-sm text-neutral-900">Email Campaigns</span>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={sendLaunchEmail} disabled={!!sendingBlast}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="send-launch-email-btn">
              {sendingBlast === "launch" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
              Invite Imported Leads
            </Button>
            <Button size="sm" variant="outline" onClick={sendDomainUpdate} disabled={!!sendingBlast}
              className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="send-domain-update-btn">
              {sendingBlast === "domain" ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Mail className="w-3.5 h-3.5 mr-1" />}
              Notify Members (Domain Update)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Source Filter Tabs */}
      <div className="flex gap-2">
        {[
          { value: "all", label: "All Leads" },
          { value: "website", label: "Website Leads" },
          { value: "imported", label: "Imported (Square)" },
        ].map(tab => (
          <Button key={tab.value} size="sm"
            variant={leadSource === tab.value ? "default" : "outline"}
            onClick={() => setLeadSource(tab.value)}
            className={leadSource === tab.value ? "bg-[#333333] text-white" : "border-neutral-300"}
            data-testid={`filter-${tab.value}`}>
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Leads List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-emerald-600" />
                Assessment Leads
              </CardTitle>
              <CardDescription>
                Leads from assessments and CRM imports
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <input type="file" accept=".csv" id="csv-import" className="hidden" onChange={handleImportCSV} data-testid="csv-import-input" />
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.getElementById("csv-import")?.click()}
                disabled={importing}
                className="border-[#39FF14]/50 text-[#2BC40E] hover:bg-[#39FF14]/10"
                data-testid="import-csv-btn"
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                {importing ? "Importing..." : "Import CSV"}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {leads.length === 0 ? (
            <div className="text-center py-8 text-neutral-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No assessment leads yet</p>
              <p className="text-sm">Leads will appear here when users complete the assessment with their email</p>
            </div>
          ) : (
            <div className="space-y-3">
              {leads.map((lead) => (
                <div 
                  key={lead.assessment_id}
                  className="border border-neutral-200 rounded-lg overflow-hidden"
                >
                  {/* Lead Header */}
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-neutral-50"
                    onClick={() => setExpandedLead(expandedLead === lead.assessment_id ? null : lead.assessment_id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`text-2xl font-bold ${getScoreColor(lead.clarity_score)}`}>
                        {lead.clarity_score}
                      </div>
                      <div>
                        <p className="font-medium text-neutral-900">{lead.name}</p>
                        <p className="text-sm text-neutral-500">{lead.business_name} • {lead.industry}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={getStatusColor(lead.status)}>
                        {lead.status}
                      </Badge>
                      <span className="text-xs text-neutral-400">
                        {formatDate(lead.created_at)}
                      </span>
                      {expandedLead === lead.assessment_id ? (
                        <ChevronUp className="w-4 h-4 text-neutral-400" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-neutral-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedLead === lead.assessment_id && (
                    <div className="border-t border-neutral-200 p-4 bg-neutral-50">
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Contact</p>
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-neutral-400" />
                            <a href={`mailto:${lead.email}`} className="text-blue-600 hover:underline">
                              {lead.email}
                            </a>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Business Stage</p>
                          <p className="capitalize">{lead.business_stage?.replace('_', ' ')}</p>
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Current Revenue</p>
                          <p className="capitalize">{lead.current_revenue?.replace(/_/g, ' ')}</p>
                        </div>
                        <div>
                          <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Revenue Goal</p>
                          <p className="capitalize">{lead.revenue_goal?.replace(/_/g, ' ')}</p>
                        </div>
                      </div>

                      <div className="mb-4">
                        <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Biggest Challenge</p>
                        <p className="capitalize font-medium text-red-600">
                          {lead.biggest_challenge?.replace(/_/g, ' ')}
                        </p>
                        <p className="text-sm text-neutral-600 mt-1">{lead.challenge_details}</p>
                      </div>

                      <div className="mb-4">
                        <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Ideal Outcome</p>
                        <p className="text-sm text-neutral-600">{lead.ideal_outcome}</p>
                      </div>

                      {lead.insights && lead.insights.length > 0 && (
                        <div className="mb-4">
                          <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">AI Insights</p>
                          <ul className="text-sm text-neutral-600 space-y-1">
                            {lead.insights.map((insight, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="text-emerald-500">•</span>
                                {insight}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* 30-Day Action Plan */}
                      {lead.thirty_day_plan && (
                        <div className="mb-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                          <p className="text-xs text-amber-700 uppercase tracking-wider mb-2 font-semibold">📅 30-Day Action Plan</p>
                          {lead.thirty_day_plan.overview && (
                            <p className="text-sm text-amber-800 mb-3 italic">{lead.thirty_day_plan.overview}</p>
                          )}
                          <div className="space-y-3">
                            {['week_1', 'week_2', 'week_3', 'week_4'].map((weekKey, idx) => {
                              const week = lead.thirty_day_plan[weekKey];
                              if (!week) return null;
                              const colors = ['border-red-400', 'border-amber-400', 'border-blue-400', 'border-green-400'];
                              return (
                                <div key={weekKey} className={`border-l-4 ${colors[idx]} pl-3 py-1`}>
                                  <p className="font-semibold text-sm text-neutral-800">{week.title}</p>
                                  <p className="text-xs text-neutral-600 mb-1"><strong>Focus:</strong> {week.focus}</p>
                                  <ul className="text-xs text-neutral-600 list-disc list-inside">
                                    {week.tasks?.slice(0, 3).map((task, i) => (
                                      <li key={i}>{task}</li>
                                    ))}
                                  </ul>
                                  <p className="text-xs text-green-700 mt-1">✓ <strong>Outcome:</strong> {week.outcome}</p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-neutral-200">
                        <Select 
                          value={lead.status}
                          onValueChange={(value) => updateLeadStatus(lead.assessment_id, value)}
                        >
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Update status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="converted">Converted</SelectItem>
                            <SelectItem value="lost">Lost</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        {/* Download PDF */}
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadPdf(lead.assessment_id, lead.name);
                          }}
                          disabled={downloadingPdf === lead.assessment_id}
                        >
                          {downloadingPdf === lead.assessment_id ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <FileDown className="w-4 h-4 mr-2" />
                          )}
                          Download PDF
                        </Button>
                        
                        {/* Resend Results Email */}
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            resendResultsEmail(lead.assessment_id, lead.email);
                          }}
                          disabled={sendingEmail === lead.assessment_id}
                          className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                        >
                          {sendingEmail === lead.assessment_id ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4 mr-2" />
                          )}
                          Send Results to Customer
                        </Button>
                        
                        {/* Convert to Client - Only show if not already converted */}
                        {lead.status !== 'converted' && lead.status !== 'proposal_sent' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              openConvertDialog(lead);
                            }}
                            className="text-purple-600 border-purple-200 hover:bg-purple-50"
                          >
                            <UserPlus className="w-4 h-4 mr-2" />
                            Convert to Client
                          </Button>
                        )}
                        
                        {/* Show proposal status if sent */}
                        {lead.status === 'proposal_sent' && (
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                            <FileText className="w-3 h-3 mr-1" />
                            NDA Proposal Sent
                          </Badge>
                        )}
                        
                        {/* Direct Email */}
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => window.open(`mailto:${lead.email}?subject=Re: Your Business Clarity Assessment`)}
                        >
                          <Mail className="w-4 h-4 mr-2" />
                          Email
                        </Button>
                        
                        {/* Delete Lead */}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              disabled={deletingLead === lead.assessment_id}
                            >
                              {deletingLead === lead.assessment_id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Lead</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete the lead for <strong>{lead.name}</strong> ({lead.business_name})? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteLead(lead.assessment_id, lead.name)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        
                        {/* Resent indicator */}
                        {lead.results_resent_at && (
                          <span className="text-xs text-neutral-400 ml-auto">
                            Results last sent: {formatDate(lead.results_resent_at)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Convert to Client Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-purple-600" />
              Convert Lead to Client
            </DialogTitle>
            <DialogDescription>
              Send an NDA proposal to {convertingLead?.name} to begin the client onboarding process.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {/* Lead Info */}
            <div className="bg-neutral-50 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="w-5 h-5 text-neutral-400" />
                <div>
                  <p className="font-medium text-neutral-900">{convertingLead?.name}</p>
                  <p className="text-sm text-neutral-500">{convertingLead?.business_name}</p>
                </div>
              </div>
              <p className="text-sm text-neutral-600">{convertingLead?.email}</p>
            </div>

            {/* Consultant Selection */}
            <div className="space-y-2">
              <Label>Assign Consultant</Label>
              <Select 
                value={selectedConsultant} 
                onValueChange={setSelectedConsultant}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a consultant to assign" />
                </SelectTrigger>
                <SelectContent>
                  {consultants.map(consultant => (
                    <SelectItem key={consultant.user_id} value={consultant.user_id}>
                      {consultant.name} ({consultant.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-neutral-500">
                This consultant will be notified when the NDA is accepted.
              </p>
            </div>

            {/* What happens next */}
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
              <h4 className="font-medium text-purple-900 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                What happens next?
              </h4>
              <ol className="text-sm text-purple-700 space-y-1 list-decimal list-inside">
                <li>Lead receives email with NDA proposal link</li>
                <li>Lead reviews and accepts the mutual NDA</li>
                <li>Client account is created with pending status</li>
                <li>Assigned consultant is notified to schedule consultation</li>
              </ol>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowConvertDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConvertToClient}
                disabled={converting || !selectedConsultant}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                {converting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send NDA Proposal
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadsPanel;
